import { Link } from '@tanstack/react-router'
import { ArrowLeft, Coins, LogOut, Plus } from 'lucide-react'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { requestCreditTopUp } from '~/entities/credits/model/credit-top-up'
import { useCreditPack } from '~/entities/credits/model/use-credit-pack'
import { useCreditsBalance } from '~/entities/credits/model/use-credits'
import { useAdminExitBusiness } from '~/entities/session/model/use-admin-auth'
import { useAuth } from '~/entities/session/model/use-auth'
import { planName } from '~/entities/subscription/plans'
import { PlanEndsNote } from '~/entities/subscription/ui/plan-ends-note'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Skeleton } from '~/shared/ui/skeleton'

interface SidebarAccountCardProps {
  isAdminViewing: boolean
  onNavigate: () => void
}

export function SidebarAccountCard({ isAdminViewing, onNavigate }: SidebarAccountCardProps) {
  const creditPack = useCreditPack()
  const { user, logout } = useAuth()
  const { mutate: exitBusiness } = useAdminExitBusiness()
  const { tier } = usePlan()
  const { data: business } = useCurrentBusiness()
  const { data: balance, isLoading: balanceLoading } = useCreditsBalance(business?.id ?? '')

  const name = business?.companyName || user?.email || ''
  const leaveLabel = isAdminViewing ? m.exitAdminView() : m.signOut()
  const credits = balanceLoading
    ? '…'
    : new Intl.NumberFormat(getLocale()).format(balance?.credits ?? 0)

  return (
    <div className="shrink-0 p-3">
      <div className="rounded-xl border border-border/70 bg-background/60 p-3">
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            <AvatarFallback className="bg-brand-subtle font-medium text-brand-subtle-foreground text-sm">
              {name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            {name ? (
              <p className="truncate font-medium text-sm leading-tight">{name}</p>
            ) : (
              <Skeleton className="h-4 w-28" />
            )}
            {user?.email && <p className="truncate text-muted-foreground text-xs">{user.email}</p>}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={leaveLabel}
            title={leaveLabel}
            onClick={() => (isAdminViewing ? exitBusiness() : logout())}
            className="rounded-full text-muted-foreground hover:text-foreground"
          >
            {isAdminViewing ? <ArrowLeft /> : <LogOut />}
          </Button>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 border-border/60 border-t pt-3">
          <Link
            to="/dashboard/subscription"
            onClick={onNavigate}
            className="inline-flex rounded-full"
          >
            <Badge variant={tier === 'free' ? 'secondary' : 'default'}>{planName(tier)}</Badge>
          </Link>
          <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground text-xs">
            <Coins className="size-3.5 shrink-0" />
            <span className="font-semibold text-foreground tabular-nums">{credits}</span>
            <span className="truncate">{m.creditsUnit()}</span>
            {creditPack ? (
              <button
                type="button"
                aria-label={m.creditPackTitle()}
                title={m.creditPackTitle()}
                onClick={() => {
                  onNavigate()
                  requestCreditTopUp()
                }}
                className="ml-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-subtle text-brand-subtle-foreground outline-none transition-colors hover:bg-brand hover:text-brand-foreground focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
              >
                <Plus className="size-3" />
              </button>
            ) : null}
          </span>
        </div>
        <PlanEndsNote className="mt-2" />
      </div>
    </div>
  )
}
